/**
 * @picnic/mech-kinematics — 공유 운동학(K층) · Bench 소유, Bench · Field 공유(FIELD-CRQ-001 D-1 · D-3 · D-4).
 *
 * <p>모터 각을 받아 관절 각 · 미끄럼 · 막힌 포트를 낸다. three 를 모른다 — 그리는 것은 부르는 쪽 몫이다
 * (Bench `features/sim/lib/board3d/build.ts` · Field 커널). 이 파일이 공개 표면이다.
 *
 * <p><b>결정론 수학</b>: K 안의 초월함수는 모두 `det-math` 로 푼다 — 어느 JS 엔진에서도 같은 비트(scripts/no-native-math.mjs 가 지킨다).
 * 버전 규칙은 README(FIELD-DEC-004 §5.1): 풀이 결과 바이트가 바뀌면 minor 이상 + {@link KINEMATICS_BUILD} 갱신.
 */
export { createMechanism, motorDirOf, type Mechanism, type RawJoint, type Solved } from "./mechanism";
export {
  clipDuration,
  jointsInSceneOrder,
  parseGltfJson,
  snapshotFromGlb,
  type Gltf,
  type JointSnapshot,
} from "./glb";
export {
  hold,
  overridden,
  parseOverrides,
  spreadLimits,
  statedLimit,
  type Limit,
  type LimitOverride,
  type LimitSpec,
} from "./limits";
export {
  makeDyad,
  makeLink,
  makeRocker,
  makeSlider,
  type Dyad,
  type Link,
  type Rocker,
  type Slider,
  type Vec3,
} from "./linkage";
export { forward, rrAngle, trackAngle, trackOffset, undoParents, unwrap } from "./chain";
export type { ChainJoint, Pose } from "./chain";
export { cycleAngle, cyclesWithCrank, makeWave, type WaveSpec } from "./wave";
export {
  createStageTilt,
  parseTiltSpec,
  tiltApproach,
  tiltLegsGoal,
  TILT_RATE,
  type StageTilt,
  type TiltLeg,
  type TiltSpec,
} from "./stage-tilt";
export { acos, atan, atan2, cos, detMath, hypot, sin, type DetMath } from "./det-math";

/**
 * 풀이 동작 식별자 — 스냅샷 · 리플레이 메타의 kinematicsVersion(FIELD SR-CMP-02).
 * <p><b>패키지 버전이 아니라 동작 바이트의 이름</b>이다. 1.0.0 의 풀이는 Field 가 쓰던 복사본(Bench 62f4f03 + det-math)과
 * 비트 단위로 같으므로(test/k-golden.test.ts) 그 이름을 그대로 둔다 — 바꾸면 같은 동작의 리플레이가 다른 버전으로 갈린다
 * (FIELD-DEC-004 §5.1). 풀이 결과가 처음 바뀌는 버전에서 `mech-kinematics@<semver>` 로 넘어간다.
 * <p>1.2.0(2026-10-07): 모터 방향(`motorDir`)으로 모터 각 → 관절 각 부호가 바뀌는 관절이 생겨 처음으로 넘어갔다.
 * motorDir 없는 옛 GLB 의 풀이 바이트는 그대로다(test/k-golden 43/43).
 */
export const KINEMATICS_BUILD = "mech-kinematics@1.2.0";
